import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { PhotoLibraryManifest, PhotoLibraryGroup, PhotoLibraryItem } from '@/lib/photoLibraryTypes';

const BUCKET = 'item-photo-library';

async function fetchManifest(): Promise<PhotoLibraryManifest> {
  const { data, error } = await supabase.storage.from(BUCKET).download('manifest.json');
  if (error) throw error;
  if (!data) throw new Error('Photo library manifest not found');
  return JSON.parse(await data.text()) as PhotoLibraryManifest;
}

/** Attaches full public URLs to every item in a list of groups. */
function resolveUrls(groups: PhotoLibraryGroup[]): PhotoLibraryGroup[] {
  return groups.map((group) => ({
    ...group,
    items: group.items.map((item) => ({
      ...item,
      url: supabase.storage.from(BUCKET).getPublicUrl(item.path).data.publicUrl,
    })),
  }));
}

export function usePhotoLibrary(category?: string) {
  const { data: manifest, isLoading, error } = useQuery({
    queryKey: ['photo-library-manifest'],
    queryFn: fetchManifest,
    staleTime: 5 * 60 * 1000, // 5 minutes — allows manifest updates to propagate
  });

  const groups = useMemo<PhotoLibraryGroup[]>(() => {
    if (!manifest) return [];
    const raw = category
      ? manifest.categories[category]?.groups ?? []
      : Object.values(manifest.categories).flatMap((c) => c.groups);
    return resolveUrls(raw);
  }, [manifest, category]);

  const allItems = useMemo<PhotoLibraryItem[]>(
    () => groups.flatMap((g) => g.items),
    [groups],
  );

  return { groups, allItems, isLoading, error: error as Error | null };
}
